/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import FaPlus from 'react-icons/lib/fa/plus';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';

// Messages
import messages from './messages/ReviewsAddCard_messages';

// Components
import IconRating from '../common/IconRating';
import LoginWidget from '../user/LoginWidget';
import Button from '../../components/Button';

const propTypes = {
  session: PropTypes.object,
  intl: intlShape.isRequired,
  report: PropTypes.object.isRequired,
  saveReview: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
};

class ReviewsAddCard extends PureComponent {
  constructor(props) {
    super(props);

    this.reviewTypes = ['overall', 'price', 'quality', 'thumbs'];

    this.state = {
      editingReview: false,
      thankForReviewing: false,
      rating: {
        overall: 0,
        price: 0,
        quality: 0,
        thumbs: 0,
      },
    };
  }

  _addReview = () => {
    this.setState({ editingReview: true });
  };

  _ratingChanged = rating => {
    this.setState({
      rating: {
        ...this.state.rating,
        [rating.themeName]: rating.rating,
      },
    });
  };

  _saveReview = () => {
    const { session } = this.props;
    const preferredOrder = ['facebook', 'google', 'zeckit'];

    const provider = preferredOrder.find(
      _provider => !!session.logins[_provider],
    );

    const login = session.logins[provider];

    this.setState({
      thankForReviewing: true,
    });

    this.props.saveReview({
      provider,
      country: this.props.report.country,
      identifier: this.props.report.identifier,
      review: this.state.rating,
      token: login.token,
    });

    this.setState({
      thankForReviewing: true,
    });
  };

  render() {
    const { intl, report, session } = this.props;
    const isLoggedIn =
      true ||
      Object.keys(session.logins).length &&
      (Object.keys(session.logins).length > 1 || !session.logins.tilaajavastuu);

    const reviewDescriptionMapping = {
      overall: intl.formatMessage(messages.ratingLabelOverall),
      price: intl.formatMessage(messages.ratingLabelPrice),
      quality: intl.formatMessage(messages.ratingLabelQuality),
      thumbs: intl.formatMessage(messages.ratingLabelThumbs),
    };

    return (
      <Container>
        {!this.state.editingReview && (
          <React.Fragment>
            <AddButton onClick={this._addReview}>
              <AddIcon />
            </AddButton>
            <Button kind="link" onClick={this._addReview}>
              <FormattedMessage
                {...messages.addNewReview}
                info="Add new review"
              />
            </Button>
          </React.Fragment>
        )}

        {!!this.state.editingReview && (
          <React.Fragment>
            {!isLoggedIn && (
              <LoginWidget
                country={this.props.report.country}
                identifier={this.props.report.identifier}
                onLogin={this.props.onLogin}
              />
            )}
            {!!isLoggedIn && (
              <React.Fragment>
                {!!this.state.thankForReviewing && (
                  <FormattedMessage
                    {...messages.thankForReviewing}
                    info="Thank for reviewing"
                  />
                )}
                {!this.state.thankForReviewing && (
                  <React.Fragment>
                    <h3>
                      <FormattedMessage
                        {...messages.rateCompany}
                        info="Rate company"
                        values={{
                          companyName:
                            report.names.display || report.names.primary,
                        }}
                      />
                    </h3>
                    {this.reviewTypes.map(type => (
                      <RatingRow key={type}>
                        <RatingType>
                          {reviewDescriptionMapping[type]}
                        </RatingType>
                        <IconRating
                          themeName={type}
                          readonly={false}
                          reviewValue={this.state.rating[type]}
                          onChange={this._ratingChanged}
                        />
                      </RatingRow>
                    ))}
                    <Button onClick={this._saveReview}>
                      <FormattedMessage
                        {...messages.saveReview}
                        info="Save review"
                      />
                    </Button>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </Container>
    );
  }
}

ReviewsAddCard.propTypes = propTypes;

const AddIcon = styled(FaPlus)`
  font-size: 28pt;
  color: ${props => props.theme['grey-dark']};
`;

const AddButton = styled(Button)`
  background: ${props => props.theme['grey-xlight']};
  border: 1px solid ${props => props.theme['grey-normal']};
  border-radius: 50%;
  box-shadow: none;
  margin: 0 auto 24px;
  padding: 24px;

  &:hover {
    box-shadow: none;
    background: ${props => props.theme['grey-dark']};

    ${AddIcon} {
      color: ${props => props.theme['grey-xlight']};
    }
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  width: 100%;
`;

const RatingRow = styled.div`
  border-bottom: 1px solid ${props => props.theme['grey-normal']};
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const RatingType = styled.h2`
  margin: 0;
`;

export default injectIntl(ReviewsAddCard);
